<template>
    <app-drawer ref="tripDrawer"
        class="trip-live-drawer"
        :drawer-id="drawerId"
        drawer-width="100vw"
        :drawer-body-class="'trip-live-drawer__body'"
        @opened="handleDrawerOpened"
        @close-modal="closeModal">
        <template slot="header">
            <div class="trip-live-drawer__header-main">
                <div class="trip-live-drawer__header-copy">
                    <span class="trip-live-drawer__eyebrow">{{ $t('live_operations') }}</span>
                    <h5 class="modal-title">{{ tripLabel }}</h5>
                    <div class="trip-live-drawer__meta">
                        <span v-if="tripData.status_name" :class="`badge badge-pill trip-live-drawer__status badge-${tripData.status_type || 'secondary'}`">
                            {{ tripData.status_name }}
                        </span>
                        <span class="trip-live-drawer__meta-pill" :class="liveTrackingStateClass">
                            {{ liveTrackingStateLabel }}
                        </span>
                        <span class="trip-live-drawer__meta-pill trip-live-drawer__meta-pill--neutral">
                            {{ trackerIdentity }}
                        </span>
                    </div>
                </div>

                <div v-if="trackerTelemetryItems.length" class="trip-live-drawer__header-telemetry">
                    <div v-for="item in trackerTelemetryItems" :key="item.key" class="trip-live-drawer__header-telemetry-item">
                        <span class="trip-live-drawer__header-telemetry-icon-wrap">
                            <app-icon :name="item.icon" class="trip-live-drawer__header-telemetry-icon" />
                        </span>

                        <div class="trip-live-drawer__header-telemetry-copy">
                            <strong>{{ item.value }}</strong>
                            <span>{{ item.label }}</span>
                        </div>
                    </div>
                </div>
            </div>
            <button type="button" class="close outline-none trip-live-drawer__close" @click.prevent="closeDrawer">
                <span class="mb-1">
                    <app-icon :name="'x'"></app-icon>
                </span>
            </button>
        </template>

        <template slot="body">
            <div class="trip-live-drawer__layout" :class="{ 'trip-live-drawer__layout--solo': !(hasEscorter || hasDriver) }">
                <section class="trip-live-drawer__main">
                    <article class="trip-live-drawer__panel trip-live-drawer__panel--map">
                        <div class="trip-live-drawer__panel-head">
                            <div>
                                <span class="trip-live-drawer__panel-kicker">{{ $t('current_position') }}</span>
                            </div>
                            <div class="trip-live-drawer__panel-actions">
                                <span v-if="recordedAtLabel" class="trip-live-drawer__panel-meta">
                                    {{ `${$t('recorded_at')}: ${recordedAtLabel}` }}
                                </span>
                                <span v-else-if="positionContextLabel" class="trip-live-drawer__panel-meta">
                                    {{ `${$t('source')}: ${positionContextLabel}` }}
                                </span>
                                <span v-else-if="!hasAssignedTracker" class="trip-live-drawer__panel-meta trip-live-drawer__panel-meta--danger">
                                    {{ $t('live_tracking_off') }}
                                </span>
                                <button type="button"
                                    class="btn btn-outline-primary trip-live-drawer__refresh-button"
                                    :class="{ 'is-loading': isPositionLoading }"
                                    :disabled="isPositionLoading || !hasAssignedTracker"
                                    @click.prevent="refreshTrackerPosition">
                                    <app-icon :name="'refresh-ccw'" class="size-14" />
                                </button>
                            </div>
                        </div>

                        <div v-if="isPositionLoading" class="trip-live-drawer__state">
                            {{ `${$t('loading')}...` }}
                        </div>

                        <template v-else-if="mapCenterCoordinates">
                            <div class="trip-live-drawer__map-toolbar">
                                <div class="trip-live-drawer__metrics">
                                    <span v-if="mapDisplayCoordinates" class="trip-live-drawer__metric">
                                        LAT {{ formatCoordinate(mapDisplayCoordinates.lat) }}
                                    </span>
                                    <span v-if="mapDisplayCoordinates" class="trip-live-drawer__metric">
                                        LNG {{ formatCoordinate(mapDisplayCoordinates.lng) }}
                                    </span>
                                    <span v-if="position && position.speed !== null && position.speed !== undefined"
                                        class="trip-live-drawer__metric">
                                        {{ `${position.speed} km/h` }}
                                    </span>
                                </div>

                                <div class="trip-live-drawer__map-toolbar-actions">
                                    <div v-if="showMapViewSwitch" class="trip-live-drawer__map-view-switch">
                                        <button
                                            type="button"
                                            class="trip-live-drawer__map-view-button"
                                            :class="{ 'trip-live-drawer__map-view-button--active': activeMapViewMode === 'tracker' }"
                                            :disabled="!canUseTrackerView"
                                            @click.prevent="setMapViewMode('tracker')">
                                            {{ $t('tracker_view') }}
                                        </button>
                                        <button
                                            type="button"
                                            class="trip-live-drawer__map-view-button"
                                            :class="{ 'trip-live-drawer__map-view-button--active': activeMapViewMode === 'journey' }"
                                            :disabled="!canUseJourneyView"
                                            @click.prevent="setMapViewMode('journey')">
                                            {{ $t('journey_view') }}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div v-if="showRouteLegend" class="trip-live-drawer__route-legend">
                                <span class="trip-live-drawer__route-legend-item">
                                    <span class="trip-live-drawer__route-legend-line trip-live-drawer__route-legend-line--covered"></span>
                                    {{ $t('covered_route') }}
                                </span>
                                <span class="trip-live-drawer__route-legend-item">
                                    <span class="trip-live-drawer__route-legend-line trip-live-drawer__route-legend-line--remaining"></span>
                                    {{ $t('remaining_route') }}
                                </span>
                            </div>

                            <div v-if="mapLoadFailed" class="trip-live-drawer__empty trip-live-drawer__empty--soft">
                                <strong>{{ $t('failed_response') }}</strong>
                                <span>{{ positionMessage || $t('no_current_position_available') }}</span>
                            </div>

                            <div v-else ref="map" class="trip-current-position-map"></div>
                        </template>

                        <div v-else class="trip-live-drawer__empty">
                            <div class="trip-live-drawer__empty-icon">
                                <i class="fa fa-map-pin"></i>
                            </div>
                            <strong>{{ mapEmptyTitle }}</strong>
                            <span>{{ mapEmptyMessage }}</span>
                        </div>
                    </article>

                    <article class="trip-live-drawer__panel trip-live-drawer__panel--brief">
                        <div class="trip-live-drawer__panel-head">
                            <div>
                                <span class="trip-live-drawer__panel-kicker">{{ $t('general_informations') }}</span>
                                <h6 class="mb-0">{{ $t('operational_brief') }}</h6>
                            </div>
                        </div>

                        <div class="trip-live-drawer__brief-layout">
                            <div class="trip-live-drawer__journey-card">
                                <div class="trip-live-drawer__journey-top">
                                    <div class="trip-live-drawer__route-card trip-live-drawer__route-card--embedded">
                                        <div class="trip-live-drawer__route-node trip-live-drawer__route-node--origin">
                                            <span>{{ $t('origin') }}</span>
                                            <strong>{{ routeOrigin }}</strong>
                                        </div>

                                        <div class="trip-live-drawer__route-connector">
                                            <span class="trip-live-drawer__route-line"></span>
                                        </div>

                                        <div class="trip-live-drawer__route-node trip-live-drawer__route-node--destination">
                                            <span>{{ $t('destination') }}</span>
                                            <strong>{{ routeDestination }}</strong>
                                        </div>
                                    </div>
                                </div>

                                <div class="trip-live-drawer__eta-card trip-live-drawer__eta-card--embedded">
                                    <div class="trip-live-drawer__eta-head">
                                        <div>
                                            <span class="trip-live-drawer__panel-kicker">{{ $t('route_progress') }}</span>
                                            <h6 class="mb-0">{{ $t('route_distance') }}</h6>
                                        </div>
                                        <span v-if="etaSourceLabel" class="trip-live-drawer__panel-meta">
                                            {{ `${$t('source')}: ${etaSourceLabel}` }}
                                        </span>
                                    </div>

                                    <div v-if="isEtaLoading" class="trip-live-drawer__state trip-live-drawer__state--compact">
                                        {{ `${$t('loading')}...` }}
                                    </div>

                                    <div v-else-if="etaEstimate" class="trip-live-drawer__eta-metrics">
                                        <div class="trip-live-drawer__eta-metric">
                                            <span>{{ $t('remaining_distance') }}</span>
                                            <strong>{{ etaEstimate.distanceText }}</strong>
                                        </div>
                                        <div v-if="etaEstimate.coveredDistanceText" class="trip-live-drawer__eta-metric">
                                            <span>{{ $t('covered_distance') }}</span>
                                            <strong>{{ etaEstimate.coveredDistanceText }}</strong>
                                        </div>
                                        <div v-if="etaEstimate.progressText" class="trip-live-drawer__eta-metric">
                                            <span>{{ $t('route_progress') }}</span>
                                            <strong>{{ etaEstimate.progressText }}</strong>
                                        </div>
                                        <div v-if="etaEstimate.totalDistanceText" class="trip-live-drawer__eta-metric">
                                            <span>{{ $t('total_distance') }}</span>
                                            <strong>{{ etaEstimate.totalDistanceText }}</strong>
                                        </div>
                                    </div>

                                    <div v-else class="trip-live-drawer__empty trip-live-drawer__empty--compact">
                                        <strong>{{ $t('eta_unavailable') }}</strong>
                                        <span v-if="etaDescription">{{ etaDescription }}</span>
                                    </div>
                                </div>
                            </div>

                            <div v-if="operationalFacts.length" class="trip-live-drawer__facts">
                                <div v-for="fact in operationalFacts" :key="fact.key" class="trip-live-drawer__fact">
                                    <span class="trip-live-drawer__fact-badge">{{ fact.badge }}</span>
                                    <div class="trip-live-drawer__fact-copy">
                                        <span>{{ fact.label }}</span>
                                        <strong>{{ fact.value || '---' }}</strong>

                                        <div v-if="fact.metaItems && fact.metaItems.length" class="trip-live-drawer__fact-meta-list">
                                            <span v-for="item in fact.metaItems" :key="item.key" class="trip-live-drawer__fact-meta-item">
                                                <small>{{ item.label }}</small>
                                                <strong>{{ item.value }}</strong>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div v-else class="trip-live-drawer__telemetry-empty">
                                {{ $t('nothing_to_show_here') }}
                            </div>
                        </div>
                    </article>
                </section>

                <aside v-if="hasEscorter || hasDriver" class="trip-live-drawer__aside">
                    <article class="trip-live-drawer__panel" v-if="hasEscorter || hasDriver">
                        <div class="trip-live-drawer__panel-head">
                            <div>
                                <span class="trip-live-drawer__panel-kicker">{{ $t('convoy_informations') }}</span>
                                <h6 class="mb-0">{{ $t('escorters') }}</h6>
                            </div>
                        </div>

                        <div v-if="hasEscorter" class="trip-live-drawer__escorter-card">
                            <div class="trip-live-drawer__escorter-layout">
                                <div class="trip-live-drawer__escorter-photo-col">
                                    <div v-if="escorterPhoto" class="trip-live-drawer__escorter-photo-frame">
                                        <img :src="escorterPhoto" :alt="escorterName || $t('escorter')" />
                                    </div>
                                    <div v-else class="trip-live-drawer__escorter-photo-fallback">
                                        <span>{{ escorterInitials }}</span>
                                    </div>
                                </div>

                                <div class="trip-live-drawer__escorter-details">
                                    <span class="trip-live-drawer__person-label">{{ $t('escorter') }}</span>
                                    <strong>{{ escorterName || '---' }}</strong>
                                    <div v-if="escorterMetaItems.length" class="trip-live-drawer__escorter-meta-list">
                                        <span v-for="item in escorterMetaItems" :key="item.key" class="trip-live-drawer__escorter-meta-item">
                                            <app-icon :name="item.icon" class="trip-live-drawer__escorter-meta-icon" />
                                            <span>{{ item.value }}</span>
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div v-if="escorterPhone" class="trip-live-drawer__contact-block">
                                <span class="trip-live-drawer__person-label">{{ $t('escorter_number') }}</span>
                                <a :href="`tel:${escorterPhone}`" class="btn btn-primary trip-live-drawer__phone-button" role="button">
                                    <app-icon :name="'phone'" class="size-14 mr-1" />
                                    {{ escorterPhone }}
                                </a>
                            </div>
                        </div>

                        <div v-if="hasDriver" class="trip-live-drawer__driver-card">
                            <div class="trip-live-drawer__driver-layout">
                                <div class="trip-live-drawer__driver-avatar">
                                    <i class="fa fa-truck"></i>
                                </div>

                                <div class="trip-live-drawer__driver-details">
                                    <span class="trip-live-drawer__person-label">{{ $t('driver') }}</span>
                                    <strong>{{ tripData.driver_name || '---' }}</strong>
                                </div>

                                <div v-if="tripData.driver_number" class="trip-live-drawer__contact-block trip-live-drawer__contact-block--driver">
                                    <span class="trip-live-drawer__person-label">{{ $t('driver_number') }}</span>
                                    <a :href="`tel:${tripData.driver_number}`" class="btn btn-primary trip-live-drawer__phone-button" role="button">
                                        <app-icon :name="'phone'" class="size-14 mr-1" />
                                        {{ tripData.driver_number }}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </article>
                </aside>
            </div>
        </template>
    </app-drawer>
</template>

<script>
import moment from 'moment';
import CoreLibrary from '../../../../core/helpers/CoreLibrary';
import { TRIP } from '../../../Config/ApiUrl';
import { getGoogleMapsErrorTranslationKey, loadGoogleMapsScript as loadGoogleMapsApiScript } from '../../../Helpers/GoogleMaps';
import { numberFormatter } from '../../../Helpers/Helpers';
import { ModalMixin } from '../../../Mixins/ModalMixin';

const MAP_RENDER_DELAY_MS = 360;
const MAP_RENDER_RETRY_DELAY_MS = 120;
const MAP_RENDER_RETRY_LIMIT = 4;
const FOCUS_CIRCLE_BASE_RADIUS = 110;
const FOCUS_CIRCLE_PULSE_RADIUS = 95;
const FOCUS_CIRCLE_PULSE_DURATION_MS = 1800;
const KAPIRI_MPOSHI_WEIGHBRIDGE_COORDINATES = Object.freeze({
    lat: -13.9249555,
    lng: 28.6628689,
});
const KAPIRI_MPOSHI_PROXIMITY_THRESHOLD_METERS = 25000;
const KAPIRI_MPOSHI_WEIGHBRIDGE_LABEL = 'Kapiri Mposhi Weighbridge';

export default {
    name: 'TripCurrentPositionModal',
    extends: CoreLibrary,
    mixins: [ModalMixin],
    props: {
        tripData: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            position: null,
            positionMessage: '',
            isPositionLoading: false,
            etaEstimate: null,
            etaMessage: '',
            isEtaLoading: false,
            etaRequestId: 0,
            mapViewMode: 'tracker',
            routeDirections: null,
            completedRouteDirections: null,
            remainingRouteDirections: null,
            journeyRouteDirections: null,
            map: null,
            mapLoadFailed: false,
            animatedMarker: null,
            focusCircle: null,
            focusCircleAnimationFrame: null,
            movementTrail: null,
            completedRoutePolyline: null,
            remainingRoutePolyline: null,
            originMarker: null,
            kapiriMarker: null,
            routeRenderer: null,
            destinationMarker: null,
            positionRequestId: 0,
            mapRenderTimeout: null,
        };
    },
    computed: {
        drawerId() {
            return `trip-current-position-modal-${this.tripData.id}`;
        },
        tripLabel() {
            const segments = [this.tripData.lot_number, this.tripData.horse].filter(Boolean);

            return segments.length ? segments.join(' / ') : `#${this.tripData.id}`;
        },
        hasAssignedTracker() {
            return Boolean(this.directTrackerName || this.directTrackerImei);
        },
        directTrackerName() {
            return this.tripData.active_device_name || '';
        },
        directTrackerImei() {
            return this.tripData.active_device_imei || '';
        },
        trackerIdentity() {
            if (!this.hasAssignedTracker) {
                return this.$t('no_device_assigned');
            }

            const trackerName = this.position && this.position.tracker_name
                ? this.position.tracker_name
                : (this.directTrackerName || this.$t('device'));
            const trackerImei = this.position && this.position.tracker_imei
                ? this.position.tracker_imei
                : this.directTrackerImei;

            return trackerImei ? `${trackerImei}` : trackerName;
        },
        liveTrackingStateLabel() {
            return this.hasAssignedTracker ? this.$t('live_tracking_on') : this.$t('live_tracking_off');
        },
        liveTrackingStateClass() {
            return this.hasAssignedTracker
                ? 'trip-live-drawer__meta-pill--success'
                : 'trip-live-drawer__meta-pill--danger';
        },
        recordedAtLabel() {
            return this.position && this.position.recorded_at
                ? this.formatRecordedAt(this.position.recorded_at)
                : '';
        },
        trackerPositionSource() {
            return this.position?.position_source || '';
        },
        positionHistory() {
            if (!this.position || !Array.isArray(this.position.position_history)) {
                return [];
            }

            return this.position.position_history
                .map((item) => ({
                    lat: Number(item.latitude),
                    lng: Number(item.longitude),
                }))
                .filter((item) => Number.isFinite(item.lat) && Number.isFinite(item.lng));
        },
        rawPayload() {
            if (!this.position || !this.position.raw_payload) {
                return {};
            }

            return typeof this.position.raw_payload === 'string'
                ? (JSON.parse(this.position.raw_payload) || {})
                : this.position.raw_payload;
        },
        trackerTelemetryItems() {
            const payload = this.rawPayload;

            return [
                {
                    key: 'imei',
                    icon: 'cpu',
                    label: this.$t('imei'),
                    value: this.formatTelemetryText(this.position?.tracker_imei || this.directTrackerImei),
                },
                {
                    key: 'battery',
                    icon: 'battery',
                    label: this.$t('battery_level'),
                    value: this.formatTelemetryBattery(payload.battery ?? this.position?.tracker_battery),
                },
                // {
                //     key: 'heading',
                //     icon: 'navigation',
                //     label: this.$t('heading'),
                //     value: this.formatTelemetryDegrees(payload.direction),
                // },
                {
                    key: 'accuracy',
                    icon: 'map-pin',
                    label: this.$t('accuracy'),
                    value: this.formatTelemetryMeters(payload.accuracy),
                },
                // {
                //     key: 'steps',
                //     icon: 'activity',
                //     label: this.$t('steps'),
                //     value: this.formatTelemetryNumber(payload.steps),
                // },
                // {
                //     key: 'heartbeat',
                //     icon: 'radio',
                //     label: this.$t('heartbeat'),
                //     value: this.formatTelemetryNumber(payload.heartbeat),
                // },
                // {
                //     key: 'ignition',
                //     icon: 'zap',
                //     label: this.$t('ignition'),
                //     value: this.formatIgnitionState(payload.accState),
                // },
                // {
                //     key: 'uptime',
                //     icon: 'activity',
                //     label: this.$t('uptime'),
                //     value: this.formatTelemetryText(payload.upt),
                // },
            ].filter((item) => this.hasTelemetryValue(item.value));
        },
        routeOrigin() {
            return this.tripData.site_name || this.tripData.location_name || '---';
        },
        routeOriginContext() {
            if (!this.tripData.location_name || !this.tripData.site_name || this.tripData.location_name === this.tripData.site_name) {
                return '';
            }

            return this.tripData.location_name;
        },
        routeDestination() {
            return this.tripData.destination_name || '---';
        },
        liveTrackerCoordinates() {
            return this.normalizeCoordinates(this.position?.latitude, this.position?.longitude);
        },
        fallbackLocationCoordinates() {
            return this.normalizeCoordinates(this.tripData.location_latitude, this.tripData.location_longitude);
        },
        trackerPositionLabel() {
            if (this.trackerPositionSource === 'live') {
                return this.$t('live_tracker_position');
            }

            return '';
        },
        trackerViewportCoordinates() {
            return this.liveTrackerCoordinates;
        },
        journeyContextCoordinates() {
            return this.liveTrackerCoordinates;
        },
        mapDisplayCoordinates() {
            return this.activeMapViewMode === 'journey'
                ? this.journeyContextCoordinates
                : this.trackerViewportCoordinates;
        },
        mapPrimaryFocusCoordinates() {
            if (this.activeMapViewMode === 'journey') {
                return this.journeyContextCoordinates || this.siteCoordinates || this.destinationCoordinates;
            }

            return this.liveTrackerCoordinates || this.siteCoordinates || this.destinationCoordinates;
        },
        mapCenterCoordinates() {
            if (this.activeMapViewMode === 'journey') {
                return this.mapDisplayCoordinates || this.siteCoordinates || this.destinationCoordinates;
            }

            return this.trackerViewportCoordinates || this.siteCoordinates || this.destinationCoordinates;
        },
        siteCoordinates() {
            return this.normalizeCoordinates(this.tripData.site_latitude, this.tripData.site_longitude);
        },
        destinationCoordinates() {
            return this.normalizeCoordinates(this.tripData.destination_latitude, this.tripData.destination_longitude);
        },
        etaSourceCoordinates() {
            return this.journeyContextCoordinates;
        },
        etaSourceLabel() {
            if (this.liveTrackerCoordinates) {
                return this.trackerPositionLabel;
            }

            return '';
        },
        positionContextLabel() {
            if (this.liveTrackerCoordinates) {
                return this.trackerPositionLabel;
            }

            return '';
        },
        etaDescription() {
            if (!this.etaMessage || this.etaMessage === this.$t('eta_unavailable')) {
                return '';
            }

            return this.etaMessage;
        },
        canUseTrackerView() {
            return Boolean(this.liveTrackerCoordinates);
        },
        canUseJourneyView() {
            return Boolean(this.destinationCoordinates && (this.siteCoordinates || this.journeyContextCoordinates || this.journeyRouteDirections || this.remainingRouteDirections));
        },
        showMapViewSwitch() {
            return this.canUseTrackerView || this.canUseJourneyView;
        },
        showRouteLegend() {
            return Boolean(this.completedRouteDirections && this.remainingRouteDirections);
        },
        activeMapViewMode() {
            if (this.mapViewMode === 'journey' && this.canUseJourneyView) {
                return 'journey';
            }

            if (this.canUseTrackerView) {
                return 'tracker';
            }

            if (this.canUseJourneyView) {
                return 'journey';
            }

            return 'tracker';
        },
        operationalFacts() {
            return [
                {
                    key: 'product',
                    badge: 'PR',
                    label: this.$t('product'),
                    value: this.tripData.product_name,
                    metaItems: [
                        {
                            key: 'net-weight',
                            label: this.$t('net_weight'),
                            value: this.formatOperationalMetric(this.tripData.net_weight, 'tonne', 'tonnes'),
                        },
                        {
                            key: 'bundles',
                            label: this.$t('bundles'),
                            value: this.formatOperationalMetric(this.tripData.bundles),
                        },
                    ].filter((item) => this.hasTelemetryValue(item.value)),
                },
                {
                    key: 'site',
                    badge: 'SI',
                    label: this.$t('site'),
                    value: this.tripData.site_name,
                },
                { key: 'transporter', badge: 'TR', label: this.$t('transporter'), value: this.tripData.transporter_name },
                { key: 'client', badge: 'CL', label: this.$t('client'), value: this.tripData.client_name },
            ].filter((fact) => fact.value);
        },
        escorterName() {
            return [
                this.tripData.escorter_first_name,
                this.tripData.escorter_middle_name,
                this.tripData.escorter_last_name,
            ].filter(Boolean).join(' ');
        },
        escorterMetaItems() {
            return [
                {
                    key: 'category',
                    icon: 'shield',
                    value: this.tripData.escorter_category ? this.$t(this.tripData.escorter_category) : '',
                },
                {
                    key: 'city',
                    icon: 'map-pin',
                    value: this.tripData.escorter_city,
                },
                {
                    key: 'matricule',
                    icon: 'credit-card',
                    value: this.tripData.escorter_matricule,
                },
            ].filter((item) => item.value);
        },
        escorterPhone() {
            return this.tripData.escorter_phone || '';
        },
        escorterPhoto() {
            return this.tripData.escorter_photo_path || '';
        },
        escorterInitials() {
            const source = this.escorterName || this.$t('escorter');

            return source
                .split(' ')
                .filter(Boolean)
                .slice(0, 2)
                .map((part) => part.charAt(0))
                .join('')
                .toUpperCase();
        },
        hasEscorter() {
            return Boolean(this.escorterName || this.escorterPhone || this.escorterPhoto);
        },
        hasDriver() {
            return Boolean(this.tripData.driver_name || this.tripData.driver_number);
        },
        mapEmptyTitle() {
            return this.hasAssignedTracker
                ? this.$t('no_live_position_available')
                : this.$t('live_tracking_off');
        },
        mapEmptyMessage() {
            if (this.positionMessage) {
                return this.positionMessage;
            }

            return this.hasAssignedTracker
                ? this.$t('failed_response')
                : this.$t('no_device_assigned');
        },
    },
    methods: {
        resetDrawerData() {
            this.clearMapRenderTimeout();
            this.position = null;
            this.positionMessage = '';
            this.isPositionLoading = false;
            this.etaEstimate = null;
            this.etaMessage = '';
            this.isEtaLoading = false;
            this.etaRequestId += 1;
            this.mapViewMode = 'tracker';
            this.routeDirections = null;
            this.completedRouteDirections = null;
            this.remainingRouteDirections = null;
            this.journeyRouteDirections = null;
            this.mapLoadFailed = false;
            this.destroyMapArtifacts();
            this.map = null;
        },
        clearMapRenderTimeout() {
            if (this.mapRenderTimeout) {
                window.clearTimeout(this.mapRenderTimeout);
                this.mapRenderTimeout = null;
            }
        },
        handleDrawerOpened() {
            this.resetDrawerData();
            this.fetchDrawerData();
        },
        closeDrawer() {
            if (this.$refs.tripDrawer && typeof this.$refs.tripDrawer.requestClose === 'function') {
                this.$refs.tripDrawer.requestClose();
                return;
            }

            this.closeModal();
        },
        formatRecordedAt(value) {
            const dateFormatMap = {
                'd-m-Y': 'DD-MM-YYYY',
                'm-d-Y': 'MM-DD-YYYY',
                'Y-m-d': 'YYYY-MM-DD',
                'm/d/Y': 'MM/DD/YYYY',
                'd/m/Y': 'DD/MM/YYYY',
                'Y/m/d': 'YYYY/MM/DD',
                'm.d.Y': 'MM.DD.YYYY',
                'd.m.Y': 'DD.MM.YYYY',
                'Y.m.d': 'YYYY.MM.DD',
            };
            const dateFormat = dateFormatMap[window.settings?.date_format] || 'DD-MM-YYYY';
            const timeFormat = `${window.settings?.time_format || 'H'}:mm:ss`;

            return moment.utc(value).format(`${dateFormat} ${timeFormat}`);
        },
        resolveThemeColor(variableName, fallback) {
            if (typeof window === 'undefined' || typeof document === 'undefined') {
                return fallback;
            }

            const color = window.getComputedStyle(document.documentElement)
                .getPropertyValue(variableName)
                .trim();

            return color || fallback;
        },
        formatCoordinate(value) {
            const numericValue = Number(value);

            return Number.isFinite(numericValue) ? numericValue.toFixed(5) : '--';
        },
        normalizeCoordinates(latitude, longitude) {
            let lat = latitude;
            let lng = longitude;

            if (latitude && typeof latitude === 'object' && longitude === undefined) {
                if (typeof latitude.lat === 'function' && typeof latitude.lng === 'function') {
                    lat = latitude.lat();
                    lng = latitude.lng();
                } else {
                    lat = latitude.lat;
                    lng = latitude.lng;
                }
            }

            lat = Number(lat);
            lng = Number(lng);

            if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
                return null;
            }

            return { lat, lng };
        },
        formatTelemetryBattery(value) {
            if (!this.hasTelemetryValue(value)) {
                return null;
            }

            return `${value}%`;
        },
        formatTelemetryDegrees(value) {
            if (!this.hasTelemetryValue(value)) {
                return null;
            }

            return `${value}°`;
        },
        formatTelemetryMeters(value) {
            if (!this.hasTelemetryValue(value)) {
                return null;
            }

            return `${value} m`;
        },
        formatTelemetryNumber(value) {
            if (!this.hasTelemetryValue(value)) {
                return null;
            }

            return `${value}`;
        },
        formatTelemetryText(value) {
            if (!this.hasTelemetryValue(value)) {
                return null;
            }

            return `${value}`;
        },
        formatIgnitionState(value) {
            if (value === null || value === undefined || value === '') {
                return null;
            }

            return Number(value) === 1 || value === true ? 'ON' : 'OFF';
        },
        formatOperationalMetric(value, unitKey = '', pluralUnitKey = '') {
            if (!this.hasTelemetryValue(value)) {
                return null;
            }

            const numericValue = Number(value);
            let formattedValue = `${value}`;

            if (!Number.isNaN(numericValue) && Number.isFinite(numericValue)) {
                formattedValue = Number.isInteger(numericValue) ? `${numericValue}` : numberFormatter(numericValue);
            }

            if (!unitKey) {
                return formattedValue;
            }

            const isPlural = !Number.isNaN(numericValue) && Number.isFinite(numericValue)
                ? Math.abs(numericValue) !== 1
                : true;
            const resolvedUnitKey = isPlural && pluralUnitKey ? pluralUnitKey : unitKey;

            return `${formattedValue} ${this.$t(resolvedUnitKey)}`;
        },
        formatDistanceValue(value) {
            const numericValue = Number(value);

            if (!Number.isFinite(numericValue)) {
                return null;
            }

            if (numericValue >= 1000) {
                const kilometers = numericValue / 1000;
                const roundedKilometers = kilometers >= 100
                    ? Math.round(kilometers)
                    : Math.round(kilometers * 10) / 10;

                return `${numberFormatter(roundedKilometers)} km`;
            }

            return `${Math.round(numericValue)} m`;
        },
        hasTelemetryValue(value) {
            return value !== null && value !== undefined && value !== '';
        },
        fetchDrawerData() {
            this.fetchCurrentPosition();
        },
        hydrateRouteInsights(requestId) {
            this.updateEtaEstimate();

            if (!this.mapCenterCoordinates) {
                return;
            }

            this.loadGoogleMapsScript()
                .then(() => {
                    if (requestId !== this.positionRequestId) {
                        return;
                    }

                    this.mapLoadFailed = false;
                    this.scheduleMapRender(requestId);
                })
                .catch((error) => {
                    if (requestId !== this.positionRequestId) {
                        return;
                    }

                    this.mapLoadFailed = true;
                    this.positionMessage = this.resolveGoogleMapsErrorMessage(error);
                });
        },
        refreshTrackerPosition() {
            this.clearMapRenderTimeout();
            this.destroyMapArtifacts();
            this.position = null;
            this.positionMessage = '';
            this.etaEstimate = null;
            this.etaMessage = '';
            this.routeDirections = null;
            this.completedRouteDirections = null;
            this.remainingRouteDirections = null;
            this.journeyRouteDirections = null;
            this.mapLoadFailed = false;
            this.mapViewMode = 'tracker';
            this.map = null;
            this.fetchCurrentPosition();
        },
        fetchCurrentPosition() {
            const requestId = ++this.positionRequestId;
            if (!this.hasAssignedTracker) {
                this.position = null;
                this.positionMessage = this.$t('no_device_assigned');
                this.isPositionLoading = false;
                this.mapLoadFailed = false;
                this.hydrateRouteInsights(requestId);
                return;
            }

            this.isPositionLoading = true;
            this.positionMessage = '';
            this.mapLoadFailed = false;

            this.axiosGet(`${TRIP}/${this.tripData.id}/current-position`, {
                params: {
                    refresh_at: Date.now(),
                },
            })
                .then((response) => {
                    if (requestId !== this.positionRequestId) {
                        return;
                    }

                    this.position = response.data;

                    if (response.data?.position_source === 'live') {
                        this.mapViewMode = 'tracker';
                    }

                    this.hydrateRouteInsights(requestId);
                })
                .catch((error) => {
                    if (requestId !== this.positionRequestId) {
                        return;
                    }

                    this.position = null;
                    this.positionMessage = error.response && error.response.data && error.response.data.message
                        ? error.response.data.message
                        : this.$t('no_current_position_available');
                    this.hydrateRouteInsights(requestId);
                })
                .finally(() => {
                    if (requestId !== this.positionRequestId) {
                        return;
                    }

                    this.isPositionLoading = false;
                });
        },
        updateEtaEstimate() {
            const requestId = ++this.etaRequestId;
            const sourceCoordinates = this.etaSourceCoordinates;
            const destinationCoordinates = this.destinationCoordinates;
            const originCoordinates = this.siteCoordinates;
            const routeCoordinates = sourceCoordinates || originCoordinates;

            if (!destinationCoordinates) {
                this.etaEstimate = null;
                this.etaMessage = this.$t('eta_requires_destination_coordinates');
                this.isEtaLoading = false;
                this.routeDirections = null;
                this.completedRouteDirections = null;
                this.remainingRouteDirections = null;
                this.journeyRouteDirections = null;
                return;
            }

            if (!routeCoordinates) {
                this.etaEstimate = null;
                this.etaMessage = this.$t('eta_requires_position_context');
                this.isEtaLoading = false;
                this.routeDirections = null;
                this.completedRouteDirections = null;
                this.remainingRouteDirections = null;
                this.journeyRouteDirections = null;
                return;
            }

            this.isEtaLoading = true;
            this.etaEstimate = null;
            this.etaMessage = '';
            this.routeDirections = null;
            this.completedRouteDirections = null;
            this.remainingRouteDirections = null;
            this.journeyRouteDirections = null;

            this.loadGoogleMapsScript()
                .then(async () => {
                    if (requestId !== this.etaRequestId || typeof google === 'undefined' || !google.maps?.DirectionsService) {
                        return;
                    }

                    let totalRoute = null;
                    let totalRouteMetrics = null;
                    let routeProgress = null;

                    if (originCoordinates) {
                        try {
                            totalRoute = await this.requestKapiriCorridorRoute(originCoordinates, destinationCoordinates);
                            totalRouteMetrics = this.summarizeRouteMetrics(totalRoute);
                        } catch (error) {
                            totalRoute = null;
                            totalRouteMetrics = null;
                        }
                    }

                    if (!totalRoute && sourceCoordinates) {
                        try {
                            totalRoute = await this.requestDrivingRoute(sourceCoordinates, destinationCoordinates);
                            totalRouteMetrics = this.summarizeRouteMetrics(totalRoute);
                        } catch (error) {
                            totalRoute = null;
                            totalRouteMetrics = null;
                        }
                    }

                    if (sourceCoordinates && totalRoute) {
                        routeProgress = this.splitRouteAtCurrentPosition(totalRoute, sourceCoordinates);
                    }

                    this.completedRouteDirections = routeProgress?.completedDirections || null;
                    this.remainingRouteDirections = routeProgress?.remainingDirections || null;
                    this.journeyRouteDirections = totalRoute;
                    this.syncActiveRouteDirections();

                    if (!totalRouteMetrics) {
                        this.etaEstimate = null;
                        this.etaMessage = this.$t('eta_unavailable');
                        this.isEtaLoading = false;

                        if (this.map && this.routeDirections) {
                            this.applyRouteToMap(this.routeDirections);
                        }

                        return;
                    }

                    const remainingDistanceValue = routeProgress?.remainingDistanceValue ?? totalRouteMetrics.distanceValue ?? null;
                    const totalDistanceValue = totalRouteMetrics?.distanceValue ?? null;
                    const coveredDistanceValue = routeProgress?.coveredDistanceValue ?? (
                        totalDistanceValue && remainingDistanceValue !== null
                            ? Math.max(0, totalDistanceValue - remainingDistanceValue)
                            : null
                    );
                    const progressValue = totalDistanceValue && remainingDistanceValue !== null
                        ? Math.max(0, Math.min(100, ((totalDistanceValue - remainingDistanceValue) / totalDistanceValue) * 100))
                        : null;

                    this.etaEstimate = {
                        durationText: totalRouteMetrics.durationText || '---',
                        distanceText: this.formatDistanceValue(remainingDistanceValue) || '---',
                        coveredDistanceText: this.formatDistanceValue(coveredDistanceValue),
                        totalDistanceText: totalRouteMetrics?.distanceText || null,
                        progressText: progressValue !== null ? `${Math.round(progressValue)}%` : null,
                    };
                    this.isEtaLoading = false;

                    if (this.map && this.routeDirections) {
                        this.applyRouteToMap(this.routeDirections);
                    }
                })
                .catch(() => {
                    if (requestId !== this.etaRequestId) {
                        return;
                    }

                    this.etaEstimate = null;
                    this.etaMessage = this.$t('eta_unavailable');
                    this.isEtaLoading = false;
                    this.routeDirections = null;
                    this.completedRouteDirections = null;
                    this.remainingRouteDirections = null;
                    this.journeyRouteDirections = null;
                });
        },
        loadGoogleMapsScript() {
            return loadGoogleMapsApiScript();
        },
        resolveGoogleMapsErrorMessage(error) {
            return this.$t(getGoogleMapsErrorTranslationKey(error));
        },
        setMapViewMode(mode) {
            if (mode !== 'tracker' && mode !== 'journey') {
                return;
            }

            this.mapViewMode = mode;
            this.syncActiveRouteDirections();
            this.refreshMapViewport();
        },
        syncActiveRouteDirections() {
            this.routeDirections = this.activeMapViewMode === 'journey'
                ? (this.journeyRouteDirections || this.remainingRouteDirections)
                : (this.remainingRouteDirections || this.journeyRouteDirections);
        },
        scheduleMapRender(requestId, attempt = 0) {
            this.clearMapRenderTimeout();

            this.$nextTick(() => {
                this.mapRenderTimeout = window.setTimeout(() => {
                    if (requestId !== this.positionRequestId || !this.mapCenterCoordinates || !this.$refs.map || this.mapLoadFailed) {
                        return;
                    }

                    const mapElement = this.$refs.map;

                    if ((mapElement.offsetWidth === 0 || mapElement.offsetHeight === 0) && attempt < MAP_RENDER_RETRY_LIMIT) {
                        this.scheduleMapRender(requestId, attempt + 1);
                        return;
                    }

                    this.initMap();
                    this.refreshMapViewport();
                }, attempt === 0 ? MAP_RENDER_DELAY_MS : MAP_RENDER_RETRY_DELAY_MS);
            });
        },
        refreshMapViewport() {
            if (!this.map || !this.mapCenterCoordinates || typeof google === 'undefined' || typeof google.maps === 'undefined') {
                return;
            }

            google.maps.event.trigger(this.map, 'resize');
            this.syncActiveRouteDirections();

            if (this.routeDirections) {
                this.applyRouteToMap(this.routeDirections);
                return;
            }

            if (this.activeMapViewMode === 'journey') {
                this.focusJourneyViewport();
                return;
            }

            this.focusTrackerViewport();
        },
        initMap() {
            if (!this.mapPrimaryFocusCoordinates || !this.$refs.map || this.mapLoadFailed) {
                return;
            }

            this.destroyMapArtifacts();
            const center = this.mapPrimaryFocusCoordinates;

            this.map = new google.maps.Map(this.$refs.map, {
                center,
                zoom: 16,
                mapTypeId: google.maps.MapTypeId.HYBRID,
                streetViewControl: false,
                mapTypeControl: true,
                mapTypeControlOptions: {
                    style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
                    position: google.maps.ControlPosition.TOP_RIGHT,
                },
                fullscreenControl: false,
                zoomControl: true,
            });

            if (this.mapDisplayCoordinates) {
                this.renderFocusCircle(this.mapDisplayCoordinates);
                this.renderAnimatedMarker(this.mapDisplayCoordinates);
            }

            this.syncActiveRouteDirections();

            if (this.routeDirections) {
                this.applyRouteToMap(this.routeDirections);
                return;
            }

            if (this.activeMapViewMode === 'journey') {
                this.focusJourneyViewport();
            } else {
                this.focusTrackerViewport();
            }
        },
        destroyMapArtifacts() {
            if (this.animatedMarker) {
                this.animatedMarker.setMap(null);
                this.animatedMarker = null;
            }

            this.stopFocusCirclePulse();

            if (this.focusCircle) {
                this.focusCircle.setMap(null);
                this.focusCircle = null;
            }

            if (this.movementTrail) {
                this.movementTrail.setMap(null);
                this.movementTrail = null;
            }

            if (this.completedRoutePolyline) {
                this.completedRoutePolyline.setMap(null);
                this.completedRoutePolyline = null;
            }

            if (this.remainingRoutePolyline) {
                this.remainingRoutePolyline.setMap(null);
                this.remainingRoutePolyline = null;
            }

            if (this.originMarker) {
                this.originMarker.setMap(null);
                this.originMarker = null;
            }

            if (this.kapiriMarker) {
                this.kapiriMarker.setMap(null);
                this.kapiriMarker = null;
            }

            if (this.routeRenderer) {
                this.routeRenderer.setMap(null);
                this.routeRenderer = null;
            }

            if (this.destinationMarker) {
                this.destinationMarker.setMap(null);
                this.destinationMarker = null;
            }
        },
        requestDrivingRoute(origin, destination, options = {}) {
            return new Promise((resolve, reject) => {
                const directionsService = new google.maps.DirectionsService();
                const waypoints = Array.isArray(options.waypoints) ? options.waypoints : [];

                directionsService.route({
                    origin,
                    destination,
                    waypoints,
                    optimizeWaypoints: false,
                    travelMode: google.maps.TravelMode.DRIVING,
                    drivingOptions: {
                        departureTime: new Date(),
                        trafficModel: google.maps.TrafficModel.BEST_GUESS,
                    },
                    provideRouteAlternatives: false,
                }, (response, status) => {
                    if (status === 'OK' && response?.routes?.length) {
                        resolve(response);
                        return;
                    }

                    reject(new Error(status || 'NO_ROUTE'));
                });
            });
        },
        async requestKapiriCorridorRoute(origin, destination) {
            if (!this.shouldForceKapiriWeighbridge(origin, destination)) {
                return this.requestDrivingRoute(origin, destination);
            }

            return this.requestDrivingRoute(origin, destination, {
                waypoints: [
                    {
                        location: KAPIRI_MPOSHI_WEIGHBRIDGE_COORDINATES,
                        stopover: false,
                    },
                ],
            });
        },
        shouldForceKapiriWeighbridge(origin, destination) {
            if (!origin || !destination) {
                return false;
            }

            const isOriginNearKapiri = this.distanceBetweenCoordinates(origin, KAPIRI_MPOSHI_WEIGHBRIDGE_COORDINATES) <= KAPIRI_MPOSHI_PROXIMITY_THRESHOLD_METERS;
            const isDestinationNearKapiri = this.distanceBetweenCoordinates(destination, KAPIRI_MPOSHI_WEIGHBRIDGE_COORDINATES) <= KAPIRI_MPOSHI_PROXIMITY_THRESHOLD_METERS;

            return !isOriginNearKapiri && !isDestinationNearKapiri;
        },
        splitRouteAtCurrentPosition(directions, currentCoordinates) {
            const fullPath = this.extractRoutePath(directions);
            const trackerPoint = this.normalizeCoordinates(currentCoordinates);

            if (!fullPath.length || !trackerPoint) {
                return null;
            }

            let closestIndex = 0;
            let closestDistance = Number.POSITIVE_INFINITY;

            fullPath.forEach((point, index) => {
                const distance = this.distanceBetweenCoordinates(point, trackerPoint);

                if (distance < closestDistance) {
                    closestDistance = distance;
                    closestIndex = index;
                }
            });

            const snappedPoint = this.normalizeCoordinates(fullPath[closestIndex]);
            const coveredRoutePath = fullPath.slice(0, closestIndex + 1);
            const remainingRoutePath = fullPath.slice(closestIndex);
            const coveredPath = [...coveredRoutePath];
            const remainingPath = [...remainingRoutePath];

            if (snappedPoint) {
                const trackerOffsetDistance = this.distanceBetweenCoordinates(trackerPoint, snappedPoint);

                if (trackerOffsetDistance > 0) {
                    coveredPath.push(trackerPoint);
                    remainingPath.unshift(trackerPoint);
                }
            }

            const totalDistanceValue = this.calculatePathDistance(fullPath);
            const routeRemainingDistanceValue = this.calculatePathDistance(remainingRoutePath);
            const trackerOffsetDistance = snappedPoint
                ? this.distanceBetweenCoordinates(trackerPoint, snappedPoint)
                : 0;
            const remainingDistanceValue = routeRemainingDistanceValue + trackerOffsetDistance;
            const coveredDistanceValue = Math.max(0, totalDistanceValue - routeRemainingDistanceValue);

            return {
                completedDirections: coveredPath.length > 1 ? this.buildSyntheticDirectionsFromPath(coveredPath) : null,
                remainingDirections: remainingPath.length > 1 ? this.buildSyntheticDirectionsFromPath(remainingPath) : null,
                coveredDistanceValue,
                remainingDistanceValue,
            };
        },
        buildSyntheticDirectionsFromPath(path) {
            if (!Array.isArray(path) || path.length < 2 || typeof google === 'undefined' || !google.maps?.LatLngBounds) {
                return null;
            }

            const bounds = new google.maps.LatLngBounds();
            const overviewPath = path
                .map((point) => this.normalizeCoordinates(point))
                .filter(Boolean)
                .map((point) => new google.maps.LatLng(point.lat, point.lng));

            overviewPath.forEach((point) => bounds.extend(point));

            return {
                routes: [
                    {
                        bounds,
                        legs: [],
                        overview_path: overviewPath,
                    },
                ],
            };
        },
        calculatePathDistance(path) {
            if (!Array.isArray(path) || path.length < 2) {
                return 0;
            }

            return path.slice(1).reduce((total, point, index) => {
                return total + this.distanceBetweenCoordinates(path[index], point);
            }, 0);
        },
        distanceBetweenCoordinates(first, second) {
            const origin = this.normalizeCoordinates(first);
            const target = this.normalizeCoordinates(second);

            if (!origin || !target) {
                return Number.POSITIVE_INFINITY;
            }

            const earthRadius = 6371000;
            const toRadians = (value) => (value * Math.PI) / 180;
            const latDelta = toRadians(target.lat - origin.lat);
            const lngDelta = toRadians(target.lng - origin.lng);
            const originLat = toRadians(origin.lat);
            const targetLat = toRadians(target.lat);
            const a = Math.sin(latDelta / 2) ** 2
                + (Math.cos(originLat) * Math.cos(targetLat) * Math.sin(lngDelta / 2) ** 2);
            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

            return earthRadius * c;
        },
        summarizeRouteMetrics(directions) {
            const legs = directions?.routes?.[0]?.legs || [];

            if (!legs.length) {
                return null;
            }

            const distanceValue = legs.reduce((total, leg) => total + (leg?.distance?.value || 0), 0);
            const durationValue = legs.reduce(
                (total, leg) => total + (leg?.duration_in_traffic?.value || leg?.duration?.value || 0),
                0
            );

            return {
                distanceValue,
                distanceText: this.formatDistanceValue(distanceValue),
                durationValue,
                durationText: this.formatDurationValue(durationValue),
            };
        },
        formatDurationValue(value) {
            const numericValue = Number(value);

            if (!Number.isFinite(numericValue) || numericValue <= 0) {
                return null;
            }

            const hours = Math.floor(numericValue / 3600);
            const minutes = Math.round((numericValue % 3600) / 60);

            if (hours > 0 && minutes > 0) {
                return `${hours}h ${minutes}min`;
            }

            if (hours > 0) {
                return `${hours}h`;
            }

            return `${minutes}min`;
        },
        applyRouteToMap(directions) {
            if (!this.map || !directions?.routes?.length) {
                return;
            }

            if (this.routeRenderer) {
                this.routeRenderer.setMap(null);
                this.routeRenderer = null;
            }

            this.renderRouteSegments(directions);
            this.renderRouteEndpoints(directions);

            if (this.activeMapViewMode === 'journey') {
                this.focusJourneyViewport(this.journeyRouteDirections || directions);
                return;
            }

            this.focusTrackerViewport();
        },
        renderRouteSegments(fallbackDirections) {
            if (!this.map || typeof google === 'undefined' || typeof google.maps === 'undefined') {
                return;
            }

            if (this.completedRoutePolyline) {
                this.completedRoutePolyline.setMap(null);
                this.completedRoutePolyline = null;
            }

            if (this.remainingRoutePolyline) {
                this.remainingRoutePolyline.setMap(null);
                this.remainingRoutePolyline = null;
            }

            const coveredColor = '#22c55e';
            const remainingColor = this.resolveThemeColor('--brand-primary', '#f97316');
            const coveredPath = this.extractRoutePath(this.completedRouteDirections);
            const remainingPath = this.extractRoutePath(this.remainingRouteDirections || fallbackDirections);

            if (coveredPath.length) {
                this.completedRoutePolyline = new google.maps.Polyline({
                    path: coveredPath,
                    geodesic: false,
                    strokeColor: coveredColor,
                    strokeOpacity: 0.9,
                    strokeWeight: 5,
                    map: this.map,
                });
            }

            if (remainingPath.length) {
                this.remainingRoutePolyline = new google.maps.Polyline({
                    path: remainingPath,
                    geodesic: false,
                    strokeColor: remainingColor,
                    strokeOpacity: 0.9,
                    strokeWeight: 5,
                    map: this.map,
                });
            }
        },
        extractRoutePath(directions) {
            if (!directions?.routes?.length) {
                return [];
            }

            const legs = directions.routes[0].legs || [];
            const detailedPath = [];

            legs.forEach((leg) => {
                (leg.steps || []).forEach((step) => {
                    const stepPath = Array.isArray(step.path) ? step.path : [];

                    if (!stepPath.length) {
                        return;
                    }

                    stepPath.forEach((point, index) => {
                        if (detailedPath.length && index === 0) {
                            const previousPoint = detailedPath[detailedPath.length - 1];

                            if (previousPoint?.equals && previousPoint.equals(point)) {
                                return;
                            }
                        }

                        detailedPath.push(point);
                    });
                });
            });

            return detailedPath.length ? detailedPath : (directions.routes[0].overview_path || []);
        },
        renderRouteEndpoints(directions) {
            const leg = directions?.routes?.[0]?.legs?.[0];

            if (!this.map) {
                return;
            }

            if (this.originMarker) {
                this.originMarker.setMap(null);
                this.originMarker = null;
            }

            if (this.kapiriMarker) {
                this.kapiriMarker.setMap(null);
                this.kapiriMarker = null;
            }

            if (this.destinationMarker) {
                this.destinationMarker.setMap(null);
                this.destinationMarker = null;
            }

            if (this.siteCoordinates && this.routeOrigin && this.routeOrigin !== '---') {
                this.originMarker = this.createMapLabelOverlay(this.siteCoordinates, this.routeOrigin, 'origin');
            } else if (leg?.start_location && this.routeOrigin && this.routeOrigin !== '---') {
                this.originMarker = this.createMapLabelOverlay(leg.start_location, this.routeOrigin, 'origin');
            }

            if (this.destinationCoordinates && this.routeDestination && this.routeDestination !== '---') {
                this.destinationMarker = this.createMapLabelOverlay(this.destinationCoordinates, this.routeDestination, 'destination');
            } else if (leg?.end_location && this.routeDestination && this.routeDestination !== '---') {
                this.destinationMarker = this.createMapLabelOverlay(leg.end_location, this.routeDestination, 'destination');
            }

            if (this.shouldForceKapiriWeighbridge(this.siteCoordinates || this.journeyContextCoordinates, this.destinationCoordinates)) {
                this.kapiriMarker = this.createMapLabelOverlay(
                    KAPIRI_MPOSHI_WEIGHBRIDGE_COORDINATES,
                    KAPIRI_MPOSHI_WEIGHBRIDGE_LABEL,
                    'waypoint'
                );
            }
        },
        createMapLabelOverlay(position, label, variant) {
            if (!this.map || !label) {
                return null;
            }

            const latLng = position instanceof google.maps.LatLng
                ? position
                : new google.maps.LatLng(position.lat, position.lng);
            const overlay = new google.maps.OverlayView();
            const safeLabel = this.escapeHtml(label);

            overlay.onAdd = function () {
                const div = document.createElement('div');
                div.className = `trip-live-drawer__map-chip trip-live-drawer__map-chip--${variant}`;
                div.innerHTML = `
                    <span class="trip-live-drawer__map-chip-dot"></span>
                    <span class="trip-live-drawer__map-chip-label">${safeLabel}</span>
                `;
                this.div = div;
                this.getPanes().overlayMouseTarget.appendChild(div);
            };

            overlay.draw = function () {
                const point = this.getProjection().fromLatLngToDivPixel(latLng);

                if (!point || !this.div) {
                    return;
                }

                this.div.style.left = `${point.x}px`;
                this.div.style.top = `${point.y}px`;
            };

            overlay.onRemove = function () {
                if (this.div && this.div.parentNode) {
                    this.div.parentNode.removeChild(this.div);
                }

                this.div = null;
            };

            overlay.setMap(this.map);

            return overlay;
        },
        escapeHtml(value) {
            return String(value)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#039;');
        },
        renderMovementTrail(center) {
            if (!this.map || typeof google === 'undefined' || typeof google.maps === 'undefined') {
                return;
            }

            const path = this.positionHistory.length > 0
                ? this.positionHistory
                : [center];

            this.movementTrail = new google.maps.Polyline({
                path,
                geodesic: true,
                strokeColor: '#f97316',
                strokeOpacity: 0.82,
                strokeWeight: 4,
                icons: [
                    {
                        icon: {
                            path: 'M 0,-1 0,1',
                            strokeOpacity: 1,
                            scale: 3,
                        },
                        offset: '0',
                        repeat: '12px',
                    },
                ],
                map: this.map,
            });
        },
        renderFocusCircle(center) {
            const themeColor = this.resolveThemeColor('--brand-primary', '#f97316');

            this.focusCircle = new google.maps.Circle({
                center,
                radius: FOCUS_CIRCLE_BASE_RADIUS,
                fillColor: themeColor,
                fillOpacity: 0.16,
                strokeColor: themeColor,
                strokeOpacity: 0.32,
                strokeWeight: 1,
                map: this.map,
            });

            this.animateFocusCircle();
        },
        animateFocusCircle() {
            if (!this.focusCircle) {
                return;
            }

            this.stopFocusCirclePulse();

            const step = (timestamp) => {
                if (!this.focusCircle) {
                    this.focusCircleAnimationFrame = null;
                    return;
                }

                const progress = (timestamp % FOCUS_CIRCLE_PULSE_DURATION_MS) / FOCUS_CIRCLE_PULSE_DURATION_MS;
                const easedProgress = 0.5 - (Math.cos(progress * Math.PI * 2) / 2);

                this.focusCircle.setRadius(
                    FOCUS_CIRCLE_BASE_RADIUS + (FOCUS_CIRCLE_PULSE_RADIUS * easedProgress)
                );
                this.focusCircle.setOptions({
                    fillOpacity: 0.18 - (0.1 * easedProgress),
                    strokeOpacity: 0.34 - (0.22 * easedProgress),
                });

                this.focusCircleAnimationFrame = window.requestAnimationFrame(step);
            };

            this.focusCircleAnimationFrame = window.requestAnimationFrame(step);
        },
        stopFocusCirclePulse() {
            if (this.focusCircleAnimationFrame) {
                window.cancelAnimationFrame(this.focusCircleAnimationFrame);
                this.focusCircleAnimationFrame = null;
            }
        },
        renderAnimatedMarker(center) {
            const latLng = new google.maps.LatLng(center.lat, center.lng);
            const marker = new google.maps.OverlayView();
            const markerImageUrl = this.getAppUrl('/images/pin.png');
            const markerLabel = this.trackerPositionSource === 'live'
                ? 'LIVE'
                : (this.liveTrackerCoordinates
                    ? (document.documentElement.lang === 'fr' ? 'TRK' : 'TRK')
                    : (this.fallbackLocationCoordinates
                        ? (document.documentElement.lang === 'fr' ? 'POS' : 'LOC')
                        : (document.documentElement.lang === 'fr' ? 'REF' : 'REF')));

            marker.onAdd = function () {
                const div = document.createElement('div');
                div.className = 'logistics-pulse-marker';
                div.innerHTML = `
                    <span class="logistics-pulse-marker__core logistics-pulse-marker__core--pin">
                        <img src="${markerImageUrl}" alt="Live position" class="logistics-pulse-marker__pin-image" />
                    </span>
                    <span class="logistics-pulse-marker__label">${markerLabel}</span>
                `;
                this.div = div;
                this.getPanes().overlayMouseTarget.appendChild(div);
            };

            marker.draw = function () {
                const point = this.getProjection().fromLatLngToDivPixel(latLng);

                if (!point || !this.div) {
                    return;
                }

                this.div.style.left = `${point.x}px`;
                this.div.style.top = `${point.y}px`;
            };

            marker.onRemove = function () {
                if (this.div && this.div.parentNode) {
                    this.div.parentNode.removeChild(this.div);
                }

                this.div = null;
            };

            marker.setMap(this.map);
            this.animatedMarker = marker;
        },
        focusTrackerViewport() {
            if (!this.map || !this.mapPrimaryFocusCoordinates || typeof google === 'undefined' || typeof google.maps === 'undefined') {
                return;
            }

            this.map.setCenter(this.mapPrimaryFocusCoordinates);
            this.map.setZoom(16);
        },
        focusJourneyViewport(directions = null) {
            if (!this.map || typeof google === 'undefined' || typeof google.maps === 'undefined') {
                return;
            }

            const bounds = new google.maps.LatLngBounds();

            if (directions?.routes?.[0]?.bounds) {
                this.map.fitBounds(directions.routes[0].bounds, 72);
            } else {
                [this.siteCoordinates, this.destinationCoordinates, this.journeyContextCoordinates].filter(Boolean).forEach((point) => {
                    bounds.extend(point);
                });

                if (bounds.isEmpty()) {
                    return;
                }

                this.map.fitBounds(bounds, 72);
            }

            google.maps.event.addListenerOnce(this.map, 'idle', () => {
                if (this.map && this.map.getZoom() > 14) {
                    this.map.setZoom(14);
                }
            });
        },
    },
    beforeDestroy() {
        this.resetDrawerData();
    },
}
</script>

<style>
.trip-live-drawer.app-drawer {
    padding: 0 !important;
}

.trip-live-drawer .app-drawer__backdrop {
    background-color: rgba(7, 16, 27, 0.52);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
}

.trip-live-drawer .app-drawer__panel {
    width: 100vw;
    max-width: 100vw;
    min-width: 100vw;
    height: 100vh;
    max-height: 100vh;
    border-radius: 28px 0 0 28px;
    border: 0;
    overflow: hidden;
    background:
        radial-gradient(circle at top left, rgba(var(--brand-primary-rgb), 0.08), transparent 28%),
        linear-gradient(180deg, #f9fbfd, #f1f5fa);
}

.trip-live-drawer .app-drawer__header {
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid rgba(15, 23, 42, 0.08);
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.92), rgba(255, 255, 255, 0.76));
}

.trip-live-drawer__body {
    padding: 0 1.5rem 1.5rem !important;
    overflow-y: auto;
    background: transparent;
}

.trip-live-drawer__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.7rem;
    min-width: 2.7rem;
    height: 2.7rem;
    border-radius: 14px;
    border: 1px solid rgba(15, 23, 42, 0.08);
    background: rgba(255, 255, 255, 0.86);
    color: #0f172a;
    opacity: 1;
    box-shadow: none;
    transition: background 180ms ease, border-color 180ms ease, color 180ms ease, transform 180ms ease;
}

.trip-live-drawer__close:hover {
    transform: translateY(-1px);
    border-color: rgba(var(--brand-primary-rgb), 0.22);
    background: rgba(var(--brand-primary-rgb), 0.08);
    color: var(--brand-primary);
}

.trip-live-drawer__header-main {
    display: grid;
    grid-template-columns: minmax(280px, auto) minmax(0, 1fr);
    gap: 1rem;
    align-items: center;
    flex: 1 1 auto;
    min-width: 0;
}

.trip-live-drawer__header-copy {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    min-width: 0;
}

.trip-live-drawer__eyebrow,
.trip-live-drawer__panel-kicker {
    color: var(--brand-primary-hover);
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
}

.trip-live-drawer__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.45rem;
    align-items: center;
}

.trip-live-drawer__meta-pill,
.trip-live-drawer__status {
    display: inline-flex;
    align-items: center;
    min-height: 2rem;
    padding: 0.4rem 0.8rem;
    border-radius: 999px;
    border: 1px solid rgba(15, 23, 42, 0.08);
    background: rgba(255, 255, 255, 0.8);
    box-shadow: none;
}

.trip-live-drawer__meta-pill {
    color: #203449;
    font-size: 0.82rem;
    font-weight: 600;
}

.trip-live-drawer__meta-pill--success {
    border-color: rgba(22, 163, 74, 0.2);
    background: rgba(22, 163, 74, 0.1);
    color: #166534;
}

.trip-live-drawer__meta-pill--danger {
    border-color: rgba(220, 38, 38, 0.18);
    background: rgba(220, 38, 38, 0.08);
    color: #b91c1c;
}

.trip-live-drawer__meta-pill--warning {
    border-color: rgba(245, 158, 11, 0.24);
    background: rgba(245, 158, 11, 0.12);
    color: #b45309;
}

.trip-live-drawer__meta-pill--neutral {
    max-width: min(22rem, 100%);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.trip-live-drawer__header-telemetry {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: minmax(8.2rem, 1fr);
    gap: 0.55rem;
    min-width: 0;
    overflow-x: auto;
    overflow-y: hidden;
    padding-bottom: 0.15rem;
}

.trip-live-drawer__header-telemetry-item {
    display: grid;
    grid-template-columns: 2.1rem minmax(0, 1fr);
    align-items: center;
    gap: 0.55rem;
    min-width: 0;
    min-height: 3rem;
    padding: 0.48rem 0.68rem;
    border-radius: 18px;
    border: 1px solid rgba(15, 23, 42, 0.08);
    background: rgba(255, 255, 255, 0.84);
}

.trip-live-drawer__header-telemetry-icon-wrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.1rem;
    height: 2.1rem;
    border-radius: 14px;
    background: rgba(var(--brand-primary-rgb), 0.08);
    color: var(--brand-primary);
}

.trip-live-drawer__header-telemetry-icon {
    width: 0.9rem;
    height: 0.9rem;
}

.trip-live-drawer__header-telemetry-copy {
    display: flex;
    flex-direction: column;
    gap: 0.12rem;
    min-width: 0;
}

.trip-live-drawer__header-telemetry-copy strong {
    color: #10233a;
    font-size: 0.88rem;
    line-height: 1.1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.trip-live-drawer__header-telemetry-copy span {
    color: #5f7184;
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.trip-live-drawer__layout {
    display: grid;
    grid-template-columns: minmax(0, 2.25fr) minmax(320px, 0.9fr);
    gap: 1rem;
    align-items: start;
}

.trip-live-drawer__layout--solo {
    grid-template-columns: minmax(0, 1fr);
}

.trip-live-drawer__main {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    min-width: 0;
}

.trip-live-drawer__aside {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    min-width: 0;
}

.trip-live-drawer__panel {
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-radius: 24px;
    background: rgba(255, 255, 255, 0.78);
    box-shadow: none;
    overflow: hidden;
}

.trip-live-drawer__panel--map,
.trip-live-drawer__panel--brief {
    padding: 1rem;
}

.trip-live-drawer__panel:not(.trip-live-drawer__panel--map):not(.trip-live-drawer__panel--brief) {
    padding: 1rem;
}

.trip-live-drawer__panel--map {
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.88), rgba(248, 250, 253, 0.92));
}

.trip-live-drawer__panel--brief {
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.94), rgba(246, 249, 252, 0.96));
}

.trip-live-drawer__panel-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 0.45rem;
}

.trip-live-drawer__panel-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 0.65rem;
}

.trip-live-drawer__panel-meta,
.trip-live-drawer__count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.32rem 0.68rem;
    border-radius: 999px;
    background: rgba(var(--brand-primary-rgb), 0.08);
    color: var(--brand-primary-hover);
    font-size: 0.78rem;
    font-weight: 700;
}

.trip-live-drawer__panel-meta--danger {
    background: rgba(220, 38, 38, 0.08);
    color: #b91c1c;
}

.trip-live-drawer__map-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.65rem;
    margin-bottom: 0.5rem;
}

.trip-live-drawer__metrics {
    display: flex;
    flex-wrap: wrap;
    gap: 0.65rem;
    margin-bottom: 0;
    flex: 1 1 auto;
}

.trip-live-drawer__map-toolbar-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 0.65rem;
}

.trip-live-drawer__map-view-switch {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 0;
}

.trip-live-drawer__route-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    margin-bottom: 0.55rem;
}

.trip-live-drawer__route-legend-item {
    display: inline-flex;
    align-items: center;
    gap: 0.42rem;
    color: #4f6275;
    font-size: 0.78rem;
    font-weight: 700;
}

.trip-live-drawer__route-legend-line {
    display: inline-block;
    width: 1.5rem;
    height: 0.22rem;
    border-radius: 999px;
}

.trip-live-drawer__route-legend-line--covered {
    background: #22c55e;
}

.trip-live-drawer__route-legend-line--remaining {
    background: var(--brand-primary);
}

.trip-live-drawer__map-view-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 2.1rem;
    padding: 0.45rem 0.82rem;
    border-radius: 999px;
    border: 1px solid rgba(15, 23, 42, 0.08);
    background: rgba(255, 255, 255, 0.9);
    color: #4f6275;
    font-size: 0.78rem;
    font-weight: 700;
    transition: background 180ms ease, border-color 180ms ease, color 180ms ease;
}

.trip-live-drawer__map-view-button--active {
    border-color: rgba(var(--brand-primary-rgb), 0.18);
    background: rgba(var(--brand-primary-rgb), 0.1);
    color: var(--brand-primary-hover);
}

.trip-live-drawer__map-view-button:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

.trip-live-drawer__metric {
    display: inline-flex;
    align-items: center;
    padding: 0.45rem 0.8rem;
    border-radius: 999px;
    border: 1px solid rgba(15, 23, 42, 0.08);
    background: rgba(255, 255, 255, 0.9);
    color: #203449;
    font-size: 0.82rem;
    font-weight: 600;
    letter-spacing: 0.04em;
}

.trip-live-drawer__refresh-button {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    min-height: 2rem;
    padding: 0.42rem 0.8rem;
    border-radius: 999px;
    font-size: 0.78rem;
    font-weight: 700;
    white-space: nowrap;
}

.trip-live-drawer__refresh-button.is-loading svg {
    animation: trip-live-drawer-spin 900ms linear infinite;
}

.trip-current-position-map {
    height: min(60vh, 600px);
    min-height: 470px;
    width: 100%;
    border-radius: 22px;
    overflow: hidden;
    border: 1px solid rgba(15, 23, 42, 0.08);
    box-shadow: none;
}

.trip-live-drawer__telemetry-empty {
    padding: 0.95rem 1rem;
    border-radius: 18px;
    border: 1px dashed rgba(15, 23, 42, 0.12);
    background: rgba(241, 245, 250, 0.72);
    color: #5f7184;
    font-size: 0.84rem;
    font-weight: 600;
}

.trip-live-drawer__brief-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
        'journey'
        'facts';
    gap: 1rem;
    align-items: start;
}

.trip-live-drawer__journey-card {
    grid-area: journey;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 0.9rem;
    padding: 1rem;
    border-radius: 20px;
    border: 1px solid rgba(15, 23, 42, 0.06);
    background: rgba(241, 245, 250, 0.78);
}

.trip-live-drawer__journey-top {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 0.75rem;
}

.trip-live-drawer__route-card {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 2.25rem minmax(0, 1fr);
    gap: 0.8rem;
    align-items: center;
    padding: 1rem;
    border-radius: 20px;
    border: 1px solid rgba(15, 23, 42, 0.06);
    background: rgba(241, 245, 250, 0.78);
}

.trip-live-drawer__route-card--embedded,
.trip-live-drawer__eta-card--embedded {
    padding: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
}

.trip-live-drawer__route-node {
    display: flex;
    flex-direction: column;
    gap: 0.22rem;
    min-width: 0;
}

.trip-live-drawer__route-node span,
.trip-live-drawer__fact-copy span {
    color: #5f7184;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.trip-live-drawer__route-node strong,
.trip-live-drawer__fact-copy strong {
    color: #10233a;
    font-size: 0.94rem;
    line-height: 1.35;
    word-break: break-word;
}

.trip-live-drawer__route-node small {
    color: #708499;
    font-size: 0.76rem;
    line-height: 1.35;
}

.trip-live-drawer__route-node--destination {
    text-align: right;
}

.trip-live-drawer__route-connector {
    position: relative;
    height: 100%;
    min-height: 2.4rem;
    display: flex;
    align-items: center;
    justify-content: center;
}

.trip-live-drawer__route-line {
    position: relative;
    display: block;
    width: 100%;
    height: 0.2rem;
    border-radius: 999px;
    background: linear-gradient(90deg, rgba(var(--brand-primary-rgb), 0.2), rgba(var(--brand-primary-rgb), 0.5));
}

.trip-live-drawer__route-line::before,
.trip-live-drawer__route-line::after {
    content: '';
    position: absolute;
    top: 50%;
    width: 0.58rem;
    height: 0.58rem;
    border-radius: 50%;
    background: #fff;
    border: 2px solid rgba(var(--brand-primary-rgb), 0.45);
    transform: translateY(-50%);
}

.trip-live-drawer__route-line::before {
    left: 0;
}

.trip-live-drawer__route-line::after {
    right: 0;
}

.trip-live-drawer__eta-card {
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
    padding: 1rem;
    border-radius: 20px;
    border: 1px solid rgba(15, 23, 42, 0.06);
    background: rgba(241, 245, 250, 0.78);
}

.trip-live-drawer__eta-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 0.75rem;
}

.trip-live-drawer__eta-metrics {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
}

.trip-live-drawer__eta-metric {
    display: flex;
    flex-direction: column;
    gap: 0.24rem;
    min-width: 0;
    padding: 0.8rem 0.9rem;
    border-radius: 16px;
    border: 1px solid rgba(15, 23, 42, 0.06);
    background: rgba(255, 255, 255, 0.78);
}

.trip-live-drawer__eta-metric span {
    color: #5f7184;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
}

.trip-live-drawer__eta-metric strong {
    color: #10233a;
    font-size: 0.92rem;
    line-height: 1.3;
    word-break: break-word;
}

.trip-live-drawer__facts {
    grid-area: facts;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.85rem;
}

.trip-live-drawer__fact {
    display: grid;
    grid-template-columns: 2.5rem minmax(0, 1fr);
    align-items: start;
    gap: 0.8rem;
    padding: 0.9rem 0.95rem;
    border-radius: 18px;
    background: rgba(241, 245, 250, 0.88);
    border: 1px solid rgba(15, 23, 42, 0.06);
}

.trip-live-drawer__fact-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 14px;
    background: rgba(var(--brand-primary-rgb), 0.08);
    color: var(--brand-primary-hover);
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.trip-live-drawer__fact-copy {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    min-width: 0;
}

.trip-live-drawer__fact-meta-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.45rem;
    margin-top: 0.35rem;
}

.trip-live-drawer__fact-meta-item {
    display: inline-flex;
    align-items: baseline;
    gap: 0.35rem;
    min-height: 1.85rem;
    padding: 0.28rem 0.58rem;
    border-radius: 999px;
    border: 1px solid rgba(15, 23, 42, 0.08);
    background: rgba(255, 255, 255, 0.74);
    white-space: nowrap;
}

.trip-live-drawer__fact-meta-item small {
    margin: 0;
    color: #708499;
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.trip-live-drawer__fact-meta-item strong {
    color: #10233a;
    font-size: 0.8rem;
    line-height: 1;
}

.trip-live-drawer__escorter-card,
.trip-live-drawer__driver-card {
    padding: 0.9rem;
    border-radius: 20px;
    background: rgba(241, 245, 250, 0.84);
    border: 1px solid rgba(15, 23, 42, 0.06);
}

.trip-live-drawer__driver-card {
    margin-top: 0.85rem;
}

.trip-live-drawer__escorter-layout {
    display: grid;
    grid-template-columns: minmax(8rem, 5fr) minmax(0, 7fr);
    gap: 1rem;
    align-items: stretch;
}

.trip-live-drawer__escorter-photo-col,
.trip-live-drawer__escorter-details {
    min-width: 0;
}

.trip-live-drawer__escorter-photo-frame,
.trip-live-drawer__escorter-photo-fallback {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 12.5rem;
    height: 100%;
    border-radius: 18px;
    overflow: hidden;
    background: #f1f5fa;
    border: 1px solid rgba(15, 23, 42, 0.06);
}

.trip-live-drawer__escorter-photo-frame img {
    display: block;
    width: 100%;
    height: auto;
    max-height: 15rem;
    object-fit: contain;
    background: #f1f5fa;
}

.trip-live-drawer__escorter-photo-fallback {
    background: linear-gradient(135deg, rgba(var(--brand-primary-rgb), 0.12), rgba(var(--brand-primary-rgb), 0.04));
    color: var(--brand-primary-hover);
    font-size: 1.6rem;
    font-weight: 700;
}

.trip-live-drawer__escorter-details,
.trip-live-drawer__driver-details {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 0.45rem;
    min-width: 0;
}

.trip-live-drawer__driver-layout {
    display: grid;
    grid-template-columns: 4.5rem minmax(0, 1fr);
    gap: 0.9rem;
    align-items: center;
}

.trip-live-drawer__driver-avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 4.5rem;
    height: 4.5rem;
    border-radius: 20px;
    background: linear-gradient(135deg, rgba(var(--brand-primary-rgb), 0.14), rgba(var(--brand-primary-rgb), 0.05));
    color: var(--brand-primary);
    font-size: 1rem;
}

.trip-live-drawer__person-label {
    color: #5f7184;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
}

.trip-live-drawer__escorter-details strong,
.trip-live-drawer__driver-details strong {
    color: #10233a;
    font-size: 0.96rem;
}

.trip-live-drawer__escorter-meta-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.trip-live-drawer__escorter-meta-item {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    min-height: 2rem;
    padding: 0.35rem 0.7rem;
    border-radius: 999px;
    border: 1px solid rgba(15, 23, 42, 0.08);
    background: rgba(255, 255, 255, 0.78);
    color: #4f6275;
    font-size: 0.78rem;
    font-weight: 600;
    line-height: 1;
}

.trip-live-drawer__escorter-meta-icon {
    width: 0.9rem;
    height: 0.9rem;
    color: var(--brand-primary);
}

.trip-live-drawer__escorter-details span,
.trip-live-drawer__escorter-details a,
.trip-live-drawer__driver-details span,
.trip-live-drawer__driver-details a {
    color: #5f7184;
    font-size: 0.84rem;
}

.trip-live-drawer__escorter-details a,
.trip-live-drawer__driver-details a {
    text-decoration: none;
}

.trip-live-drawer__contact-block {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(15, 23, 42, 0.08);
    min-width: 0;
}

.trip-live-drawer__contact-block--driver {
    grid-column: 1 / -1;
}

.trip-live-drawer__phone-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    align-self: flex-start;
    gap: 0.35rem;
    min-height: 2.4rem;
    padding: 0.55rem 0.9rem;
    border-radius: 999px;
    font-size: 0.84rem;
    font-weight: 600;
    line-height: 1;
    text-decoration: none !important;
    white-space: nowrap;
}

.trip-live-drawer__state,
.trip-live-drawer__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.6rem;
    min-height: 220px;
    padding: 1.25rem;
    border-radius: 22px;
    text-align: center;
}

.trip-live-drawer__state {
    color: #5f7184;
    font-weight: 600;
    background: rgba(241, 245, 250, 0.74);
}

.trip-live-drawer__empty {
    border: 1px dashed rgba(15, 23, 42, 0.12);
    background: rgba(241, 245, 250, 0.66);
    color: #5f7184;
}

.trip-live-drawer__empty--soft {
    min-height: 180px;
}

.trip-live-drawer__state--compact,
.trip-live-drawer__empty--compact {
    min-height: 160px;
}

.trip-live-drawer__empty-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3.5rem;
    height: 3.5rem;
    border-radius: 50%;
    background: rgba(217, 67, 67, 0.1);
    color: #d94343;
    font-size: 1.6rem;
}

.logistics-pulse-marker {
    position: absolute;
    width: 42px;
    height: 54px;
    transform: translate(-50%, -100%);
    pointer-events: none;
}

.logistics-pulse-marker__core {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 54px;
    box-shadow: none;
}

.logistics-pulse-marker__core--pin {
    background: transparent;
}

.logistics-pulse-marker__pin-image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
    filter: drop-shadow(0 8px 12px rgba(7, 16, 27, 0.16));
}

.logistics-pulse-marker__label {
    position: absolute;
    top: -30px;
    left: 50%;
    transform: translateX(-50%);
    padding: 0.22rem 0.45rem;
    border-radius: 999px;
    background: rgba(9, 21, 36, 0.9);
    color: #fff;
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    box-shadow: none;
}

.trip-live-drawer__map-chip {
    position: absolute;
    display: inline-flex;
    align-items: center;
    gap: 0.38rem;
    max-width: 14rem;
    padding: 0.32rem 0.56rem;
    border-radius: 999px;
    border: 1px solid rgba(15, 23, 42, 0.08);
    background: rgba(255, 255, 255, 0.96);
    color: #10233a;
    font-size: 0.72rem;
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;
    transform: translate(-8%, -145%);
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
}

.trip-live-drawer__map-chip--destination {
    transform: translate(-92%, -145%);
}

.trip-live-drawer__map-chip--waypoint {
    transform: translate(-50%, -165%);
}

.trip-live-drawer__map-chip--waypoint .trip-live-drawer__map-chip-dot {
    background: #2563eb;
}

.trip-live-drawer__map-chip-dot {
    flex: 0 0 auto;
    width: 0.56rem;
    height: 0.56rem;
    border-radius: 50%;
    background: var(--brand-primary);
}

.trip-live-drawer__map-chip-label {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
}

[theme='dark'] .trip-live-drawer .app-drawer__backdrop {
    background-color: rgba(2, 6, 23, 0.62);
}

[theme='dark'] .trip-live-drawer .app-drawer__panel {
    background:
        radial-gradient(circle at top left, rgba(var(--brand-primary-rgb), 0.14), transparent 28%),
        linear-gradient(180deg, #08111d, #0f1723);
}

[theme='dark'] .trip-live-drawer .app-drawer__header {
    border-bottom-color: rgba(148, 163, 184, 0.14);
    background: linear-gradient(180deg, rgba(15, 23, 42, 0.94), rgba(15, 23, 42, 0.82));
}

[theme='dark'] .trip-live-drawer__close {
    border-color: rgba(148, 163, 184, 0.18);
    background: rgba(15, 23, 42, 0.84);
    color: #eff6ff;
}

[theme='dark'] .trip-live-drawer__meta-pill,
[theme='dark'] .trip-live-drawer__status,
[theme='dark'] .trip-live-drawer__map-view-button,
[theme='dark'] .trip-live-drawer__metric,
[theme='dark'] .trip-live-drawer__fact-meta-item,
[theme='dark'] .trip-live-drawer__escorter-meta-item {
    border-color: rgba(148, 163, 184, 0.16);
    background: rgba(15, 23, 42, 0.78);
    color: #dbe7f4;
}

[theme='dark'] .trip-live-drawer__meta-pill--warning {
    border-color: rgba(245, 158, 11, 0.24);
    background: rgba(120, 53, 15, 0.35);
    color: #fde68a;
}

[theme='dark'] .trip-live-drawer__header-telemetry-item,
[theme='dark'] .trip-live-drawer__panel,
[theme='dark'] .trip-live-drawer__journey-card,
[theme='dark'] .trip-live-drawer__route-card,
[theme='dark'] .trip-live-drawer__eta-card,
[theme='dark'] .trip-live-drawer__fact,
[theme='dark'] .trip-live-drawer__escorter-card,
[theme='dark'] .trip-live-drawer__driver-card,
[theme='dark'] .trip-live-drawer__escorter-photo-frame,
[theme='dark'] .trip-live-drawer__escorter-photo-fallback,
[theme='dark'] .trip-live-drawer__state,
[theme='dark'] .trip-live-drawer__empty,
[theme='dark'] .trip-live-drawer__telemetry-empty {
    border-color: rgba(148, 163, 184, 0.14);
    background: rgba(15, 23, 42, 0.74);
}

[theme='dark'] .trip-live-drawer__panel--map {
    background: linear-gradient(180deg, rgba(15, 23, 42, 0.88), rgba(15, 23, 42, 0.78));
}

[theme='dark'] .trip-live-drawer__panel--brief {
    background: linear-gradient(180deg, rgba(15, 23, 42, 0.92), rgba(15, 23, 42, 0.82));
}

[theme='dark'] .trip-current-position-map,
[theme='dark'] .trip-live-drawer__eta-metric,
[theme='dark'] .trip-live-drawer__contact-block {
    border-color: rgba(148, 163, 184, 0.14);
}

[theme='dark'] .trip-live-drawer__header-telemetry-copy strong,
[theme='dark'] .trip-live-drawer__route-node strong,
[theme='dark'] .trip-live-drawer__eta-metric strong,
[theme='dark'] .trip-live-drawer__fact-copy strong,
[theme='dark'] .trip-live-drawer__fact-meta-item strong,
[theme='dark'] .trip-live-drawer__escorter-details strong,
[theme='dark'] .trip-live-drawer__driver-details strong {
    color: #eff6ff;
}

[theme='dark'] .trip-live-drawer__header-telemetry-copy span,
[theme='dark'] .trip-live-drawer__route-node span,
[theme='dark'] .trip-live-drawer__route-node small,
[theme='dark'] .trip-live-drawer__eta-metric span,
[theme='dark'] .trip-live-drawer__fact-copy span,
[theme='dark'] .trip-live-drawer__fact-meta-item small,
[theme='dark'] .trip-live-drawer__person-label,
[theme='dark'] .trip-live-drawer__escorter-details span,
[theme='dark'] .trip-live-drawer__escorter-details a,
[theme='dark'] .trip-live-drawer__driver-details span,
[theme='dark'] .trip-live-drawer__driver-details a,
[theme='dark'] .trip-live-drawer__state,
[theme='dark'] .trip-live-drawer__empty,
[theme='dark'] .trip-live-drawer__telemetry-empty {
    color: #9fb0c5;
}

[theme='dark'] .trip-live-drawer__route-line::before,
[theme='dark'] .trip-live-drawer__route-line::after {
    background: #0f1723;
    border-color: rgba(var(--brand-primary-rgb), 0.52);
}

[theme='dark'] .trip-live-drawer__map-view-button--active {
    background: rgba(var(--brand-primary-rgb), 0.18);
    color: #eff6ff;
}

[theme='dark'] .trip-live-drawer__route-legend-item {
    color: #9fb0c5;
}

[theme='dark'] .trip-live-drawer__map-chip {
    border-color: rgba(148, 163, 184, 0.18);
    background: rgba(15, 23, 42, 0.92);
    color: #eff6ff;
    box-shadow: 0 10px 24px rgba(2, 6, 23, 0.34);
}

[theme='dark'] .trip-live-drawer__escorter-photo-frame img {
    background: #0f1723;
}

@keyframes trip-live-drawer-spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

@media (max-width: 991px) {
    .trip-live-drawer .app-drawer__panel {
        min-width: 100vw;
        width: 100vw;
        max-width: 100vw;
    }

    .trip-live-drawer .app-drawer__panel {
        border-radius: 0;
    }

    .trip-live-drawer__layout {
        grid-template-columns: minmax(0, 1fr);
    }

    .trip-live-drawer__header-main,
    .trip-live-drawer__brief-layout {
        grid-template-columns: minmax(0, 1fr);
        grid-template-areas:
            'journey'
            'facts';
    }

    .trip-live-drawer__eta-head {
        flex-direction: column;
    }

    .trip-live-drawer__aside {
        order: 2;
    }

    .trip-live-drawer__escorter-layout {
        grid-template-columns: minmax(0, 1fr);
    }
}

@media (max-width: 767px) {
    .trip-live-drawer .app-drawer__header,
    .trip-live-drawer__body {
        padding-left: 1rem !important;
        padding-right: 1rem !important;
    }

    .trip-live-drawer__panel-head {
        flex-direction: column;
        align-items: flex-start;
    }

    .trip-live-drawer__header-telemetry {
        grid-auto-columns: minmax(7.1rem, 1fr);
    }

    .trip-live-drawer__map-toolbar {
        align-items: flex-start;
    }

    .trip-live-drawer__map-toolbar-actions {
        justify-content: flex-start;
    }

    .trip-live-drawer__facts {
        grid-template-columns: minmax(0, 1fr);
    }

    .trip-live-drawer__eta-metrics {
        grid-template-columns: minmax(0, 1fr);
    }

    .trip-live-drawer__route-card {
        grid-template-columns: minmax(0, 1fr);
    }

    .trip-live-drawer__route-node--destination {
        text-align: left;
    }

    .trip-live-drawer__route-connector {
        min-height: 1.4rem;
    }

    .trip-live-drawer__route-line {
        height: 2.2rem;
        width: 0.2rem;
        background: linear-gradient(180deg, rgba(var(--brand-primary-rgb), 0.2), rgba(var(--brand-primary-rgb), 0.5));
    }

    .trip-live-drawer__route-line::before,
    .trip-live-drawer__route-line::after {
        left: 50%;
        right: auto;
        transform: translate(-50%, -50%);
    }

    .trip-current-position-map {
        height: min(52vh, 480px);
        min-height: 340px;
    }

    .trip-live-drawer__route-line::before {
        top: 0;
    }

    .trip-live-drawer__route-line::after {
        top: 100%;
    }

    .trip-current-position-map {
        height: 300px;
    }
}
</style>
